/**
 * Index
 * Location Autocomplete
 * @author Rhys Lloyd
 */

// Utilities
import debug from "debug";
const MODULE_NAME = 'location-autocomplete';
const log = debug(MODULE_NAME);
//
// Dependencies
import queryString from 'query-string';
import googleMapsApiPromise from '../../services/google-maps-api';

//
// Constants
import {
	AUTOCOMPLETE_OPTIONS,
	AUTOCOMPLETE_INPUT_SELECTOR,
	AUTOCOMPLETE_INPUT_DATA_ATTRIBUTE,
	AUTOCOMPLETE_SYNC_KEY,
	LOCATION_FIELD_CONTEXT,
	LOCATION_FIELD_SELECTED_PLACE
} from './config';

//
// Key codes
const KEY_ENTER = 13;

//
// Functions

/**
 * Updates provided inputs with the respective place
 * @param {google.maps.places.PlaceResult} place
 * @param {HTMLInputElement} latInput
 * @param {HTMLInputElement} lngInput
 * @param {HTMLInputElement} gpsInput
 */
function updateInputsFromAutocomplete (place, latInput, lngInput, gpsInput) {
	const lat = place.geometry.location.lat(),
		lng = place.geometry.location.lng();

	log('Updating lat/lng', lat, lng);
	latInput.value = lat.toString();
	lngInput.value = lng.toString();
	//noinspection JSValidateTypes
	gpsInput.value = 0;
}

/**
 * Creates a hidden input field.
 * @param name
 * @returns {HTMLInputElement}
 */
function createHiddenInput(name) {
	const field = /** @type {HTMLInputElement} */ document.createElement('input');
	field.type = 'hidden';
	field.name = name;
	return field;
}

/**
 * Initialises a HTML Input with Google Places Autocomplete
 * @param {google} google
 * @param {HTMLInputElement} input
 */
function initialiseAutocompleteInput (google, input) {
	log('Initialise autocomplete on input:', input);
	const autocomplete = new google.maps.places.Autocomplete(input, AUTOCOMPLETE_OPTIONS);

	if (!input.form) {
		log('No form found for input', input);
		return;
	}

	const latInput = createHiddenInput('lat'),
		lngInput = createHiddenInput('lng'),
		gpsInput = createHiddenInput('gps');

	const parsedQueryString = queryString.parse(location.search);
	latInput.value = parsedQueryString.lat;
	lngInput.value = parsedQueryString.lng;
	gpsInput.value = 0;

	const inputContainer = input.parentNode;
	inputContainer.insertBefore(latInput, input);
	inputContainer.insertBefore(lngInput, input);
	inputContainer.insertBefore(gpsInput, input);

	// State Flag - not an ideal solution, but haven't found a better one yet...
	let submitAfterChange = false;

	input.addEventListener('keydown', (event) => {
		if (event.which !== KEY_ENTER) {
			gpsInput.value = 0;
			return;
		}

		// QCUTWO-361
		// If the user presses Enter to select a location, the form is submitted before the
		// 'place_changed' event can fire and do its thing. Here we're just momentarily delaying
		// the submission until after the 'place_changed' has fired.
		event.preventDefault();
		submitAfterChange = true;
	});

	autocomplete.addListener('place_changed', () => {
		const place = autocomplete.getPlace();
		$(input).data(LOCATION_FIELD_SELECTED_PLACE, place);
		log('Place changed to', place);

		// Place hasn't been selected from the dropdown, we have no information.
		if (!place.geometry) {
			return;
		}

		updateInputsFromAutocomplete(place, latInput, lngInput, gpsInput);
		$(input).trigger('change');

		input.form.submit();
	});

	return {
		fields: {
			search: input,
			lat: latInput,
			lng: lngInput,
			gps: gpsInput,
		},
		form: input.form,
		autocomplete: autocomplete,
	};
}

//
// Export
export function exec () {
	log('Running...');	

	const $inputs = $(AUTOCOMPLETE_INPUT_SELECTOR);

	if (!$inputs.length) {		
		return;
	}

	// Get loading Places API ASAP
	googleMapsApiPromise
		.then((google) => {
			let $searchInputs = $([]);
			let $latInputs = $([]);
			let $lngInputs = $([]);
			let $gpsInputs = $([]);

			$(AUTOCOMPLETE_INPUT_SELECTOR).each((index, /** @type HTMLInputElement */ input) => {
				//noinspection JSCheckFunctionSignatures
				const context = initialiseAutocompleteInput(google, input);
				const $input = $(input);
				$input.data(LOCATION_FIELD_CONTEXT, context);

				if (input.getAttribute(AUTOCOMPLETE_INPUT_DATA_ATTRIBUTE) !== AUTOCOMPLETE_SYNC_KEY) {
					return;
				}

				$searchInputs = $searchInputs.add($input);
				$latInputs = $latInputs.add($(context.fields.lat));
				$lngInputs = $lngInputs.add($(context.fields.lng));
				$gpsInputs = $gpsInputs.add($(context.fields.gps));
			});

			// Set up value syncing
			$searchInputs.on('change', function () {
				const $changedInput = $(this);
				const context = $changedInput.data(LOCATION_FIELD_CONTEXT);
				const place = $changedInput.data(LOCATION_FIELD_SELECTED_PLACE);

				log('Updating location fields with context:', context);
				$searchInputs.val(context.fields.search.value)
					.data(LOCATION_FIELD_SELECTED_PLACE, place);
				$latInputs.val(context.fields.lat.value);
				$lngInputs.val(context.fields.lng.value);
				$gpsInputs.val(context.fields.gps.value);
			});
			
		}, (err) => {
			log(err);			
		});
}

export {exec as default};



// WEBPACK FOOTER //
// ./project/client/src/core/modules/location-autocomplete/index.js